import { useEffect, useMemo, useState } from 'react'
import { Button, Dialog, DialogTitle, Typography } from '@mui/material'
import axios from 'axios'
import { format } from 'date-fns'
import ReactGA from 'react-ga4'
import { useNavigate } from 'react-router-dom'
import { AppConstants, LeadStage } from 'AppConstants/AppConstants'
import Footer from 'components/Footer'
import Header from 'components/Header'
import useQueryParams from 'hooks/useQueryParams'
import {
  SetPurchasedPlanSignUp,
  SetTimeLeftToExpireSignUp,
  SetPaymentDoneSignUp,
  SetSelectedSlotSignUp,
} from 'redux/SignUp/persist'
import { useAppDispatch, useAppSelector } from 'redux/store'
import { createNavigateWithQueryParams } from 'utils/navigateWithQueryParams'
import styles from './Confirmation.module.scss'

const ConfirmationSignUp = () => {
  const styless: any = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 300,
    height: 170,
    overflow: 'hidden',
    backgroundColor: '#050624',
    color: 'white',
    borderRadius: '8px',
    p: 4,
  }

  const { getAllParams, getParams, hasCampaign, hasContent, hasParams } = useQueryParams()
  const utm_source = getParams('utm_source')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Signup flow state management
  const appointmentTime = useAppSelector((state) => state.signUpPersist.selectedSlotSignUp)
  const paymentDone = useAppSelector((state) => state.signUpPersist.paymentDoneSignUp)
  const plans = useAppSelector((state) => state.signUpPersist.availablePlansSignUp)
  const visitCount = useAppSelector((state) => state.signUpPersist.visitCountSignUp)
  const purchasedPlan = useAppSelector((state) => state.signUpPersist.purchasedPlanSignUp)
  const selectedPlan = useAppSelector((state) => state.signUpPersist.selectedPlanSignUp)
  const studios = useAppSelector((state) => state.signUpPersist.studiosSignUp)
  const selectedStudio = useAppSelector((state) => state.signUpPersist.selectedStudioSignUp)
  const timeLeft: any = useAppSelector((state) => state.signUpPersist.timeLeftToExpireSignUp)
  const trackerCount = useAppSelector((state) => state.signUpPersist.trackerCountSignUp)
  const locParam = getParams('loc')
  // Create reusable navigation function
  const navigateWithQueryParams = createNavigateWithQueryParams(navigate, getAllParams)

  useEffect(() => {
    if (!selectedStudio) {
      // No studio selected (or session state was lost): restart at studio selection, then slot, then plan.
      dispatch(SetSelectedSlotSignUp(null))
      navigateWithQueryParams('/signup/location')
    }
  }, [])
  const [error, setError] = useState(false)
  const [slotNotFound, setSlotNotFound] = useState(false)

  const plan = useMemo(() => {
    if (plans) {
      const filterOnDemand = plans?.filter((p) => p.type === 'on-demand')
      return filterOnDemand
    } else {
      return null
    }
  }, [plans])

  const subscriptionType = useMemo(() => {
    if (plans) {
      const filterSubscription = plans?.filter((p) => p.type === 'subscription')
      return filterSubscription
    } else {
      return null
    }
  }, [plans])

  const schedulePhysicalVisit = async () => {
    if (appointmentTime?.scheduleObject) {
      const response: any = await axios
        .post(AppConstants.API_URL + '/visit/chiropractor/appointment', appointmentTime.scheduleObject, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
            'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
          },
        })
        .then((res) => {
          return res
        })
        .catch((err) => {
          return err.response
        })

      if (response?.data?.code === 'success') {
        // ReactGA.event('conversion', {
        //   send_to: process.env.REACT_APP_GA_CONVERSION_ID || 'AW-11337579902/40e2CN_xnqkZEP76lp4q',
        //   value: plan && plan[0] && plan[0]?.amount > 0 ? plan[0].amount / 100 : 0,
        //   currency: 'USD',
        // })
        navigateWithQueryParams('/signup/done')
      } else {
        if (response?.data?.code === 'slot_booked_already') {
          setError(true)
        } else if (response?.data?.code === 'slot_not_found') {
          setSlotNotFound(true)
        }
        return false
      }
    } else {
      return false
    }
  }

  // const purchaseFreeCredit = async () => {
  //   try {
  //     const response: any = await axios
  //       .post(`${AppConstants.API_URL}/visit/chiropractor/free-appointment`, null, {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem('userToken')}`,
  //           'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
  //         },
  //       })
  //       .then((res) => {
  //         console.log(res?.data.code, 'res?.data.code')
  //         if (res && res?.data.code === 'success') {
  //           schedulePhysicalVisit()
  //         }
  //       })
  //       .catch((err) => {
  //         if (err) {
  //           alert('Something went wrong. Please try again.')
  //         }
  //       })
  //   } catch (error) {
  //     console.error(error)
  //   }
  // }

  async function purchasePlan(selectedPlanId: string | undefined) {
    try {
      const response: any = await axios
        .post(
          `${AppConstants.API_URL}/payment-plan/purchase`,
          { planId: selectedPlanId, quantity: 1 },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('userToken')}`,
              'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
            },
          },
        )
        .then((res) => {
          if (res && res?.data && res?.data?.data) {
            dispatch(SetPurchasedPlanSignUp(res?.data?.data))
            navigateWithQueryParams('/signup/payment')
          }
        })
        .catch((err) => {
          if (err) {
            alert('Something went wrong. Please try again.')
          }
        })
    } catch (error) {
      console.error(error)
    }
  }

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`
  }

  const postLeadStage = async () => {
    await axios
      .post(
        AppConstants.API_URL + '/lead/stage/update',
        { stage: LeadStage.confirmation },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
            'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
          },
        },
      )
      .then((res) => {
        return res
      })
      .catch((err) => {
        return err?.response
      })
  }

  useEffect(() => {
    postLeadStage()
  }, [])

  useEffect(() => {
    ReactGA.event('page_view', {
      page_title: 'SignUp|Confirmation',
      page_location: '/signup/confirmation',
    })
  }, [])

  useEffect(() => {
    dispatch(SetTimeLeftToExpireSignUp(timeLeft ?? 299))
  }, [])

  useEffect(() => {
    if (timeLeft > 0) {
      const timerId = setInterval(() => {
        dispatch(SetTimeLeftToExpireSignUp(timeLeft && timeLeft - 1))
      }, 1000)

      return () => clearInterval(timerId)
    }
  }, [timeLeft])

  return (
    <>
      <div className={styles.container}>
        <Header
          headerText=""
          showBackButton={true}
          onBack={() => {
            if (!hasCampaign) {
              navigateWithQueryParams('/signup/plan')
            } else {
              navigateWithQueryParams('/signup/slot')
            }
          }}
        />
        <div className={styles.locationContainer}>
          <div className={styles.confirmHeader}>Confirm your appointment.</div>
          {studios && (
            <div className={styles.locationDetails}>
              <div className={styles.locationTextContainer}>
                <div className={styles.locationWrapper}>
                  <div className={styles.appointmentTime}>
                    {appointmentTime
                      ? format(new Date(appointmentTime?.scheduleObject.appointmentTime), 'EEEE, MMM dd')
                      : ''}
                    <div>
                      {appointmentTime
                        ? format(new Date(appointmentTime?.scheduleObject.appointmentTime), 'h:mm a')
                        : ''}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 400 }}>Exam + Adjustment</div>
                  </div>
                  <div>
                    <div className={styles.locationText} style={{ fontWeight: 700 }}>
                      {selectedStudio?.name}
                    </div>
                    <div className={styles.locationText}>{selectedStudio?.address}</div>

                    <div className={styles.cityAndState}>
                      <div style={{ marginRight: '4px' }}>{selectedStudio?.city.name + ','}</div>
                      <div style={{ marginRight: '4px', textTransform: 'uppercase' }}>{selectedStudio?.state}</div>
                      <div>{selectedStudio?.zipCode}</div>
                    </div>
                  </div>
                </div>
              </div>
              <img
                style={{ width: '145px', height: '150px', objectFit: 'cover' }}
                className={styles.mapImageItem}
                src={selectedStudio?.mapImage || ''}
                onClick={() => {
                  if (selectedStudio?.mapLocation) {
                    window.open(selectedStudio.mapLocation, '_blank')
                  }
                  //   window.location.href =
                  //     'https://www.google.com/maps/place/302+Mott+St,+New+York,+NY+10012,+USA/@40.7248592,-73.9940347'
                }}
                alt="map"
              />
            </div>
          )}

          {error && (
            <Dialog
              onClose={() => {
                // setError(false)
              }}
              open={error}
              PaperProps={{
                style: {
                  background: 'var(--color-background)',
                  borderRadius: '12px',
                  padding: '10px 12px',
                  minWidth: 300,
                  maxWidth: 300,
                  minHeight: 'auto',
                  // height: 170,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  flex: 1, // align everything to the left
                  textAlign: 'left', // global text alignment
                },
              }}
            >
              <DialogTitle
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: 'var(--color-text-primary)',
                  textAlign: 'left',
                  marginBottom: '12px',
                  padding: 0,
                  width: '100%',
                }}
              >
                Time Limit Reached
              </DialogTitle>
              <Typography
                style={{
                  color: 'var(--color-text-primary)',
                  fontSize: '14px',
                  textAlign: 'left',
                  marginBottom: '24px',
                  lineHeight: 1.5,
                  width: '100%',
                }}
              >
                Your booking session expired. Please select another available slot to continue.
              </Typography>
              <Button
                color="primary"
                style={{
                  marginBottom: '0.3rem',
                  height: '42px',
                  padding: '0px 8px 0px 8px',
                  fontSize: '14px',
                  alignSelf: 'center',
                }}
                onClick={() => {
                  setError(false)
                  navigateWithQueryParams('/signup/slot')
                }}
              >
                Reschedule
              </Button>
            </Dialog>
          )}
          {slotNotFound && (
            <Dialog
              onClose={() => {
                // setError(false)
              }}
              open={slotNotFound}
              PaperProps={{
                style: {
                  background: 'var(--color-background)',
                  borderRadius: '12px',
                  padding: '10px 12px',
                  minWidth: 300,
                  maxWidth: 300,
                  minHeight: 'auto',
                  // height: 170,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  flex: 1, // align everything to the left
                  textAlign: 'left', // global text alignment
                },
              }}
            >
              <DialogTitle
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: 'var(--color-text-primary)',
                  textAlign: 'left',
                  marginBottom: '12px',
                  padding: 0,
                  width: '100%',
                }}
              >
                Time Limit Reached
              </DialogTitle>
              <Typography
                style={{
                  color: 'var(--color-text-primary)',
                  fontSize: '14px',
                  textAlign: 'left',
                  marginBottom: '24px',
                  lineHeight: 1.5,
                  width: '100%',
                }}
              >
                Your booking session expired. Please select another available slot to continue.
              </Typography>
              <Button
                color="primary"
                style={{
                  marginBottom: '0.3rem',
                  height: '42px',
                  padding: '0px 8px 0px 8px',
                  fontSize: '14px',
                  alignSelf: 'center',
                }}
                onClick={() => {
                  setSlotNotFound(false)
                  navigateWithQueryParams('/signup/slot')
                }}
              >
                Reschedule
              </Button>
            </Dialog>
          )}
        </div>
        {!hasCampaign && (
          <div className={styles.cancellationPolicy}>
            <div className={styles.policyHeader}>Appointment Change Policy</div>
            <div className={styles.cancellation}>
              <strong>Cancellations:</strong> Cancellations made at least 12 hours before the appointment time will not
              incur a fee. Cancellations made between 12 hours and 1 hour before the appointment will incur a $10 fee,
              and those made less than 1 hour before, or in the case of a no-show, will incur a $20 fee.<br></br>
              <br></br>
              <strong>Same-Day Reschedules:</strong> Appointments can be rescheduled for the same day at no charge if
              done at least 1 hour in advance. Same-day reschedules made within 1 hour of the appointment time will
              incur a $20 short-notice fee.<br></br>
              <br></br>
              These policies ensure fair access for all Members and allow our doctors to provide care to other patients
              in need.
            </div>
          </div>
        )}
        {plan && plan[0]?.amount !== 0 && hasCampaign ? (
          <div className={styles.offerDetails}>
            <span className={styles.offerDetailsHeader}>New Patient Special Policy</span>
            <div className={styles.offerText}>
              A non-refundable payment is required to reserve your appointment. Please complete your payment to confirm
              your booking.
            </div>
          </div>
        ) : null}
        {plan && plan[0]?.amount === 0 && hasCampaign && (
          <div className={styles.cardRequiredPolicy}>
            <div className={styles.policyHeader}>Card Required to Confirm Appointment</div>
            <div className={styles.cardRequired}>
              A credit card is required to secure your free booking. Your card won't be charged today. It's only used if
              you non-show or cancel. No-shows or cancellations will result in a $40 non-refundable charge. This policy
              ensures fair access to slots for all patients while respecting our doctor's time.
            </div>
          </div>
        )}
        {hasParams && hasCampaign ? (
          <div className={styles.offerExpires}>
            Offer Expires In: <span className={styles.timer}>{formatTime(timeLeft || 0)}</span>
          </div>
        ) : null}
        <Footer
          buttonText={
            plan && plan[0]?.amount === 0
              ? 'Add Card to Confirm'
              : (!paymentDone && plan && plan[0]?.amount > 0) || (selectedPlan && selectedPlan?.amount > 0)
              ? !hasCampaign
                ? `Confirm and Pay $${selectedPlan && selectedPlan?.amount / 100}`
                : `Confirm and Pay $${plan && plan[0]?.amount / 100}`
              : 'Confirm'
          }
          isLoading={false}
          trackerIndex={!hasCampaign ? 7 : locParam ? 5 : 6}
          shouldShowFour={true}
          shouldShowFive={true}
          shouldShowSix={true}
          shouldShowSeven={trackerCount >= 7}
          shouldShowEight={trackerCount >= 8}
          isDisabled={false}
          buttonType={'button'}
          onButtonClick={() => {
            if (plan && plan[0]?.amount === 0 && appointmentTime?.scheduleObject) {
              navigateWithQueryParams('/signup/payment-setup')
              // purchaseFreeCredit()
            } else if (appointmentTime?.scheduleObject && plan && plan[0]?.amount > 0) {
              if (!paymentDone) {
                if (!hasCampaign) {
                  purchasePlan(selectedPlan?.id)
                } else {
                  purchasePlan(plan[0]?.id)
                }
              } else {
                schedulePhysicalVisit()
              }
            } else if (appointmentTime?.scheduleObject && subscriptionType && subscriptionType[0]?.amount > 0) {
              if (!paymentDone) {
                purchasePlan(selectedPlan?.id)
              } else {
                schedulePhysicalVisit()
              }
            }
          }}
        />
      </div>
    </>
  )
}

export default ConfirmationSignUp
